import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Power, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { tierMeta, type AlertTier } from "@/lib/alertStyles";
import { cn } from "@/lib/utils";
import { AlertEditorModal, type AlertEditorValue } from "./AlertEditorModal";

interface AlertRow {
  id: string;
  tier: AlertTier;
  title: string;
  description: string;
  app_wide: boolean;
  page_keys: string[];
  active: boolean;
  created_at: string;
}

export function AlertsManager() {
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AlertEditorValue | null>(null);
  const [detail, setDetail] = useState<AlertRow | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("system_alerts" as any)
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error("Kunde inte hämta meddelanden");
    else setAlerts((data as any) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (a: AlertRow) => {
    const { error } = await supabase
      .from("system_alerts" as any)
      .update({ active: !a.active })
      .eq("id", a.id);
    if (error) toast.error("Misslyckades");
    else {
      toast.success(!a.active ? "Aktiverat" : "Inaktiverat");
      setDetail(null);
      load();
    }
  };

  const remove = async (a: AlertRow) => {
    if (!confirm("Radera detta meddelande permanent?")) return;
    const { error } = await supabase.from("system_alerts" as any).delete().eq("id", a.id);
    if (error) toast.error("Misslyckades");
    else {
      toast.success("Raderat");
      setDetail(null);
      load();
    }
  };

  const openEdit = (a: AlertRow) => {
    setEditing({
      id: a.id,
      tier: a.tier,
      title: a.title,
      description: a.description,
      app_wide: a.app_wide,
      page_keys: a.page_keys,
    });
    setDetail(null);
    setEditorOpen(true);
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Publicera systemövergripande meddelanden som visas som banner högst upp.
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/admin/alerts-test">
              <FlaskConical className="mr-2 h-4 w-4" />
              Testsida
            </Link>
          </Button>
          <Button
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Publicera nytt meddelande
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-muted-foreground py-8">Laddar...</p>
      ) : alerts.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">Inga meddelanden ännu.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {alerts.map((a) => {
            const meta = tierMeta[a.tier];
            return (
              <button
                key={a.id}
                onClick={() => setDetail(a)}
                className={cn(
                  "relative text-left rounded-lg border p-4 transition-all hover:shadow-md",
                  a.active
                    ? "border-green-300 bg-green-50 dark:bg-green-950/30 dark:border-green-800"
                    : "border-border bg-muted/40 opacity-80",
                )}
              >
                <Badge
                  variant="outline"
                  className={cn("absolute top-2 left-2 text-[10px]", meta.badge)}
                >
                  {meta.emoji} {meta.label}
                </Badge>
                <Badge
                  variant="outline"
                  className={cn(
                    "absolute top-2 right-2 text-[10px]",
                    a.active
                      ? "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/50 dark:text-green-100"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {a.active ? "Aktiv" : "Inaktiv"}
                </Badge>
                <div className="mt-8">
                  <p className="font-semibold text-sm line-clamp-2">{a.title}</p>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-3">
                    {a.description}
                  </p>
                  <p className="mt-3 text-[10px] text-muted-foreground">
                    {a.app_wide ? "Appövergripande" : `${a.page_keys.length} sida(or)`} ·{" "}
                    {new Date(a.created_at).toLocaleDateString("sv-SE")}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <AlertEditorModal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={editing}
        onSaved={load}
      />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className={cn("text-[10px]", tierMeta[detail.tier].badge)}>
                    {tierMeta[detail.tier].emoji} {tierMeta[detail.tier].label}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[10px]",
                      detail.active
                        ? "bg-green-100 text-green-800 border-green-300"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {detail.active ? "Aktiv" : "Inaktiv"}
                  </Badge>
                </div>
                <DialogTitle>{detail.title}</DialogTitle>
                <DialogDescription>
                  Publicerad {new Date(detail.created_at).toLocaleString("sv-SE")} ·{" "}
                  {detail.app_wide ? "Appövergripande" : `${detail.page_keys.length} sida(or)`}
                </DialogDescription>
              </DialogHeader>

              <p className="text-sm whitespace-pre-wrap">{detail.description}</p>

              {!detail.app_wide && detail.page_keys.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-muted-foreground mb-1.5">Visas på:</p>
                  <div className="flex flex-wrap gap-1">
                    {detail.page_keys.map((k) => (
                      <Badge key={k} variant="secondary" className="text-[10px]">
                        {k}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-3">
                <Button variant="outline" onClick={() => toggleActive(detail)}>
                  <Power className="mr-2 h-4 w-4" />
                  {detail.active ? "Inaktivera" : "Aktivera"}
                </Button>
                <Button variant="outline" onClick={() => openEdit(detail)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Redigera
                </Button>
                <Button variant="destructive" onClick={() => remove(detail)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Radera
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
